import { Row } from '@/app/components/Flex/Flex'
import { Input } from '@/app/components/Input/Input'
import { Layout } from '@/app/components/Layout/Layout'
import { Motion } from '@/app/components/Motion/Motion'
import { RadioInput } from '@/app/components/RadioInput/RadioInput'
import { Spinner } from '@/app/components/Spinner/Spinner'
import { Terms } from '@/app/components/Terms/Terms'
import { Text } from '@/app/components/Text/Text'
import { Lib } from '@/app/components/UI/Lib'
import { UI } from '@/app/components/UI/UI'
import { useNavigate } from '@/app/hooks/useNavigate'
import { useSession } from '@/app/state/session'
import { api } from '@/app/utils/api'
import { parseError } from '@/app/utils/error'
import type { FunctionalComponent } from 'preact'
import { useState } from 'preact/hooks'
import styles from './index.module.scss'

const toggleValue = (values: string[], value: string) => (
  values.includes(value)
    ? values.filter(item => item !== value)
    : [...values, value]
)

enum View {
  Form,
  Result,
}

export const AnketaPage: FunctionalComponent = () => {
  const route = useNavigate()
  const userEmail = useSession(s => s.user?.email)
  const fetchUser = useSession(s => s.fetch)

  const [view, setView] = useState(View.Form)
  const [age, setAge] = useState('')
  const [gender, setGender] = useState('')
  const [interests, setInterests] = useState<string[]>([])
  const [goals, setGoals] = useState<string[]>([])
  const [otherGoal, setOtherGoal] = useState('')
  const [experience, setExperience] = useState('')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  const valid = Boolean(
    age
      && gender
      && interests.length > 0
      && (goals.length > 0 || otherGoal.trim())
      && experience,
  )

  const submit = async () => {
    if (!valid || loading) return

    try {
      setLoading(true)
      await api.post('/anketa', {
        age,
        gender,
        interests,
        goals,
        otherGoal,
        experience,
      })
      if (userEmail) {
        fetchUser()
        setView(View.Result)
      }
      else {
        route('~/login?from=anketa')
      }
    }
    catch (e) {
      console.error(e)
      setErrors(parseError(e))
    }
    finally {
      setLoading(false)
    }
  }

  return (
    <Layout>
      <Layout.Content>
        <Layout.Header>
          Анкета ZoeAstro
        </Layout.Header>

        {view === View.Result
          ? (
            <Motion key='result'>
              <Row gap={15}>
                <UI.Panel>
                  <Text
                    fontSize={16}
                  >
                    Спасибо за ответы! 💜 Нам очень важно ваше мнение — оно помогает делать ZoeAstro полезнее и удобнее. Мы начислили тебе
                    10 бесплатных сообщений.
                  </Text>
                </UI.Panel>

                <UI.Button
                  type='primary'
                  link='~/tools'
                >
                  Перейти к разборам
                </UI.Button>
              </Row>
            </Motion>
          )
          : (
            <Row gap={15}>
              <UI.Panel>
                <Text
                  fontSize={16}
                >
                  Пройдите короткий опрос (около 2 минут) и получите 10 бесплатных сообщений для знакомства с ZoeAstro. Нам важно узнать
                  ваши интересы, чтобы сделать сервис лучше ✨
                </Text>
              </UI.Panel>

              <UI.Panel>
                <Row gap={15} alignItems='flex-start'>
                  <Text
                    fontSize={16}
                    fontWeight={600}
                  >
                    Сколько вам лет?
                  </Text>

                  <RadioInput
                    checked={age === '18-24'}
                    value='18-24'
                    label='18-24'
                    type='radio'
                    onChange={() => setAge('18-24')}
                  />

                  <RadioInput
                    checked={age === '25-34'}
                    value='25-34'
                    label='25-34'
                    type='radio'
                    onChange={() => setAge('25-34')}
                  />

                  <RadioInput
                    checked={age === '35-44'}
                    value='35-44'
                    label='35-44'
                    type='radio'
                    onChange={() => setAge('35-44')}
                  />

                  <RadioInput
                    checked={age === '45-54'}
                    value='45-54'
                    label='45-54'
                    type='radio'
                    onChange={() => setAge('45-54')}
                  />

                  <RadioInput
                    checked={age === '55+'}
                    value='55+'
                    label='55+'
                    type='radio'
                    onChange={() => setAge('55+')}
                  />
                </Row>
              </UI.Panel>

              <UI.Panel>
                <Row gap={15} alignItems='flex-start'>
                  <Text
                    fontSize={16}
                    fontWeight={600}
                  >
                    Укажите ваш пол
                  </Text>

                  <RadioInput
                    checked={gender === 'Женский'}
                    value='Женский'
                    label='Женский'
                    type='radio'
                    onChange={() => setGender('Женский')}
                  />

                  <RadioInput
                    checked={gender === 'Мужской'}
                    value='Мужской'
                    label='Мужской'
                    type='radio'
                    onChange={() => setGender('Мужской')}
                  />

                  <RadioInput
                    checked={gender === 'Предпочитаю не указывать'}
                    value='Предпочитаю не указывать'
                    label='Предпочитаю не указывать'
                    type='radio'
                    onChange={() => setGender('Предпочитаю не указывать')}
                  />
                </Row>
              </UI.Panel>

              <UI.Panel>
                <Row gap={15} alignItems='flex-start'>
                  <Row gap={3}>
                    <Text
                      fontSize={16}
                      fontWeight={600}
                    >
                      Какие темы вам интересны
                    </Text>

                    <Text
                      fontSize={14}
                      opacity={0.5}
                    >
                      Можно выбрать несколько из списка
                    </Text>
                  </Row>

                  <RadioInput
                    checked={interests.includes('Астрология')}
                    value='Астрология'
                    label='Астрология'
                    type='check'
                    onChange={() => setInterests(values => toggleValue(values, 'Астрология'))}
                  />

                  <RadioInput
                    checked={interests.includes('Таро')}
                    value='Таро'
                    label='Таро'
                    type='check'
                    onChange={() => setInterests(values => toggleValue(values, 'Таро'))}
                  />

                  <RadioInput
                    checked={interests.includes('Нумерология')}
                    value='Нумерология'
                    label='Нумерология'
                    type='check'
                    onChange={() => setInterests(values => toggleValue(values, 'Нумерология'))}
                  />

                  <RadioInput
                    checked={interests.includes('Психология и самопознание')}
                    value='Психология и самопознание'
                    label='Психология и самопознание'
                    type='check'
                    onChange={() => setInterests(values => toggleValue(values, 'Психология и самопознание'))}
                  />

                  <RadioInput
                    checked={interests.includes('Гороскопы и прогнозы')}
                    value='Гороскопы и прогнозы'
                    label='Гороскопы и прогнозы'
                    type='check'
                    onChange={() => setInterests(values => toggleValue(values, 'Гороскопы и прогнозы'))}
                  />

                  <RadioInput
                    checked={interests.includes('Ничего из перечисленного')}
                    value='Ничего из перечисленного'
                    label='Ничего из перечисленного'
                    type='check'
                    onChange={() => setInterests(values => toggleValue(values, 'Ничего из перечисленного'))}
                  />
                </Row>
              </UI.Panel>

              <UI.Panel>
                <Row gap={15} alignItems='flex-start'>
                  <Text
                    fontSize={16}
                    fontWeight={600}
                  >
                    С каким вопросом вы в первую очередь обратились бы к ZoeAstro?
                  </Text>

                  <RadioInput
                    checked={goals.includes('Любовь и отношения')}
                    type='check'
                    value='Любовь и отношения'
                    label='Любовь и отношения'
                    onChange={() => setGoals(values => toggleValue(values, 'Любовь и отношения'))}
                  />

                  <RadioInput
                    checked={goals.includes('Совместимость с партнёром')}
                    type='check'
                    value='Совместимость с партнёром'
                    label='Совместимость с партнёром'
                    onChange={() => setGoals(values => toggleValue(values, 'Совместимость с партнёром'))}
                  />

                  <RadioInput
                    checked={goals.includes('Деньги и карьера')}
                    type='check'
                    value='Деньги и карьера'
                    label='Деньги и карьера'
                    onChange={() => setGoals(values => toggleValue(values, 'Деньги и карьера'))}
                  />

                  <RadioInput
                    checked={goals.includes('Будущее и предстоящие события')}
                    type='check'
                    value='Будущее и предстоящие события'
                    label='Будущее и предстоящие события'
                    onChange={() => setGoals(values => toggleValue(values, 'Будущее и предстоящие события'))}
                  />

                  <RadioInput
                    checked={goals.includes('Самопознание и личностный рост')}
                    type='check'
                    value='Самопознание и личностный рост'
                    label='Самопознание и личностный рост'
                    onChange={() => setGoals(values => toggleValue(values, 'Самопознание и личностный рост'))}
                  />

                  <RadioInput
                    checked={goals.includes('Решение конкретной ситуации')}
                    type='check'
                    value='Решение конкретной ситуации'
                    label='Решение конкретной ситуации'
                    onChange={() => setGoals(values => toggleValue(values, 'Решение конкретной ситуации'))}
                  />

                  <RadioInput
                    checked={goals.includes('Другое')}
                    type='check'
                    value='Другое'
                    label='Другое'
                    onChange={() => setGoals(values => toggleValue(values, 'Другое'))}
                  />

                  <div class={styles.input}>
                    <Input
                      radius='sm'
                      placeholder='Напиши свой вариант'
                      value={otherGoal}
                      onInput={setOtherGoal}
                    />
                  </div>
                </Row>
              </UI.Panel>

              <UI.Panel>
                <Row gap={15} alignItems='flex-start'>
                  <Text
                    fontSize={16}
                    fontWeight={600}
                  >
                    Пользовались ли вы раньше услугами астрологов, тарологов, нумерологов или другими похожими онлайн-сервисами?
                  </Text>

                  <RadioInput
                    checked={experience === 'Да, платными услугами'}
                    type='radio'
                    value='Да, платными услугами'
                    label='Да, платными услугами'
                    onChange={() => setExperience('Да, платными услугами')}
                  />

                  <RadioInput
                    checked={experience === 'Да, но только бесплатными'}
                    type='radio'
                    value='Да, но только бесплатными'
                    label='Да, но только бесплатными'
                    onChange={() => setExperience('Да, но только бесплатными')}
                  />

                  <RadioInput
                    checked={experience === 'Да, и платными, и бесплатными'}
                    type='radio'
                    value='Да, и платными, и бесплатными'
                    label='Да, и платными, и бесплатными'
                    onChange={() => setExperience('Да, и платными, и бесплатными')}
                  />

                  <RadioInput
                    checked={experience === 'Нет'}
                    type='radio'
                    value='Нет'
                    label='Нет'
                    onChange={() => setExperience('Нет')}
                  />
                </Row>
              </UI.Panel>

              <Lib.Errors errors={errors} />

              <UI.Button
                type='primary'
                disabled={!valid || loading}
                onClick={() => void submit()}
                gap={5}
              >
                Отправить
                {loading && <Spinner size={20} />}
              </UI.Button>

              <UI.Hint>
                <Terms btn='Отправить' />
              </UI.Hint>
            </Row>
          )}
      </Layout.Content>
    </Layout>
  )
}
